/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import cx from 'classnames';
import React from 'react';
import { useTheme, SvgCheckmarkSmall, SvgImportantSmall, } from '../../utils';
import '@itwin/itwinui-css/css/progress-indicator.css';
/**
 * Circular Progress Indicator
 * @example
 * Basic
 * <ProgressRadial value={50} />
 * Indeterminate
 * <ProgressRadial indeterminate />
 * Positive / Negative
 * <ProgressRadial status='positive' />
 * <ProgressRadial status='negative' />
 * Centered Content
 * <ProgressRadial value={63}>63</ProgressRadial>
 * Small
 * <ProgressRadial size={'small'} indeterminate/>
 */
export const ProgressRadial = (props) => {
    const { value = 0, indeterminate = false, status, children, size = '', className, style, ...rest } = props;
    useTheme();
    const statusMap = {
        negative: React.createElement(SvgImportantSmall, { "aria-hidden": true }),
        positive: React.createElement(SvgCheckmarkSmall, { "aria-hidden": true }),
    };
    const fillStyle = {
        strokeDashoffset: status === 'positive'
            ? 0
            : 88 -
                Math.min(88, Math.max(0, indeterminate ? 88 : (88 * value) / 100)),
    };
    return (React.createElement("div", { className: cx('iui-progress-indicator-radial', {
            'iui-determinate': !indeterminate,
            'iui-indeterminate': indeterminate,
            [`iui-${size}`]: !!size,
            [`iui-${status}`]: !!status,
        }, className), style: style, ...rest },
        React.createElement("svg", { className: 'iui-radial', viewBox: '0 0 32 32', "aria-hidden": 'true' },
            React.createElement("circle", { className: 'iui-track', cx: '16', cy: '16', r: '14' }),
            React.createElement("circle", { className: 'iui-fill', cx: '16', cy: '16', r: '14', style: fillStyle })),
        React.createElement(React.Fragment, null,
            status && (React.createElement("span", { className: 'iui-inner-content' }, statusMap[status])),
            !status && children && (React.createElement("span", { className: 'iui-inner-content' }, children)))));
};
export default ProgressRadial;
